import { injectable } from 'inversify';
import Debug from 'debug';
import { Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { createUpserter, mergeRightToLeft } from '@/app/shared/utils'
import { defaultQuestionAttempts, QuestionAttempts } from './question-attempts'
import { QuestionAttemptQuery } from './question-attempt-query'

const debug = Debug('question-attempt-store-module');

export type RemoteStateQueryPhase = 'none' | 'loading' | 'loaded' | 'error';

const upserter = createUpserter(defaultQuestionAttempts);

export const QuestionAttemptStoreModuleSymbol = Symbol.for('QuestionAttemptStoreModule');

interface AddAttemptPayload {
  id: string
  attempt: {
    thinkingTimeSeconds: number
    achievedScore: number
  }
}

@injectable()
@Module({
  namespaced: true,
  name: 'question-attempt',
  store,
  dynamic: true
})
export class QuestionAttemptStoreModule extends VuexModule implements QuestionAttemptQuery {

  private remoteStateQueryPhase: RemoteStateQueryPhase = 'none';
  private localState: QuestionAttempts[] = []
  private remoteState: QuestionAttempts[] = []

  @Mutation
  public addAttempts(attempts: AddAttemptPayload[]) {
    debug('add attempt: ', attempts)

    for (let item of attempts) {      
      this.localState = upserter(this.localState, item.id, (current) => ({
        id: current.id,
        attempts: [ ...current.attempts, {
          thinkingTimeSeconds: item.attempt.thinkingTimeSeconds,
          achievedScore: item.attempt.achievedScore
        } ]
      }))
    }

    debug('attempt added, new state: ', this.localState)
  }

  get query() {
    const self = this
    return function(questionIds: string[]): QuestionAttempts[] {

      debug('query', questionIds)
      
      const attempts = self.attempts
      const result: QuestionAttempts[] = []
      for (let id of questionIds) {
        const found = attempts.find(x => x.id === id)
        if (!found) { continue }

        result.push({
          id: found.id,
          attempts: [ ...found.attempts ]
        })
      }

      debug('query result', result)
      return result
    }
  }

  get attempts() {
    debug('question attempts', this.remoteState, this.localState)
    const merged = mergeRightToLeft(this.remoteState, this.localState, merge)
    debug('merged', merged)

    return merged

    function merge(remote: QuestionAttempts, local: QuestionAttempts) {
      const attempts = [ ...remote.attempts, ...local.attempts ]
      return {
        id: local.id,
        attempts
      }
    }
  }

  @Mutation
  public startRemoteQuerying() {
    this.remoteStateQueryPhase = 'loading';
    this.remoteState = [];
  }

  @Mutation
  public finishRemoteQuerying(results: QuestionAttempts[]) {
    this.remoteState = results;
    this.remoteStateQueryPhase = 'loaded';
  }
}

export default getModule(QuestionAttemptStoreModule);
